import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allArticles } from '@/mocks/seoData';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuy.beauty';

function buildArticleSchema(article: typeof allArticles[0]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.sections[0]?.content?.slice(0, 160) || article.title,
    'image': article.image,
    'author': {
      '@type': 'Person',
      'name': article.author,
    },
    'datePublished': article.date,
    'dateModified': article.date,
    'publisher': {
      '@type': 'Organization',
      'name': 'ItaobuySpreadsheet',
      'url': siteUrl,
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-500 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildArticleSchema(article)) }}
      />
      <Navbar />

      <main className="pt-20">
        {/* Article Header */}
        <article className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                Home
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">Shopping Guide</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-500 truncate max-w-[200px]">{article.shortTitle}</span>
            </nav>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-xs text-foreground-400 mb-8">
              <span>{article.author}</span>
              <span className="text-foreground-300">|</span>
              <span>{article.date}</span>
            </div>

            {/* Internal link to homepage */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                Back to Home
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Start Shopping
              </a>
            </div>

            {/* Article image */}
            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.imageAlt}
                className="w-full h-auto object-cover rounded-2xl"
                title={article.title}
              />
            </div>

            {/* Article sections */}
            <div className="flex flex-col gap-8">
              {article.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {section.content}
                  </p>
                </section>
              ))}
            </div>

            {/* Internal links at bottom */}
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                Continue Exploring
              </h4>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-foreground-900 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Homepage
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  Try ItaobuySpreadsheet Now
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>

            {/* Related articles */}
            <div className="mt-10 pt-8 border-t border-background-200/60">
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-5">
                More Shopping Guides
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allArticles
                  .filter((a) => a.slug !== slug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="flex items-center gap-3 p-4 rounded-xl bg-background-50 border border-background-200/50 hover:border-primary-200/50 transition-all cursor-pointer group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                        <i className="ri-article-line text-primary-600 text-sm"></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors leading-tight">
                        {a.shortTitle}
                      </span>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related CTA */}
        <section className="w-full px-6 md:px-10 py-14 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
              Ready to Start Spreadsheet Shopping?
            </h3>
            <p className="text-sm text-foreground-500 mb-6">
              Experience the smartest way to source products from China with the ItaobuySpreadsheet system.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-7 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Itaobuy Sheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-7 py-3 border border-foreground-300/40 text-foreground-700 font-medium text-sm rounded-full hover:bg-foreground-50 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}